import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { mdiAccountCancel } from '@lumapps/lumx/icons';
import { Size, IconSizes, Theme, HeadingElement } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { BaseState } from '../BaseState';

import './index.scss';

export interface RestrictedAccessStateProps {
    /** custom classname */
    className?: string;
    /** custom description to display */
    description?: string;
    /** icon size to display - default to xxl */
    iconSize?: IconSizes;
    /** whether this component is vertically centered on the page or not */
    isVerticallyCentered?: boolean;
    /** whether the description shoud be displayed or not */
    shouldShowDescription?: boolean;
    /** whether the icon should be displayed or not */
    shouldShowIcon?: boolean;
    /** theme */
    theme?: Theme;
    /** title to display */
    title?: string;
    /** override for the html to use for rendering the node */
    titleAs?: HeadingElement;
    /** props to be passed in to the component wrapper */
    wrapperProps?: React.HTMLAttributes<HTMLDivElement>;
}

const CLASSNAME = 'lumx-restricted-access-state';
/**
 * Reusable component in order to display that something prevents the user from having access to this page or part of it
 *
 * @family States
 * @param props RestrictedAccessStateProps
 * @returns RestrictedAccessState
 */
export const RestrictedAccessState: React.FC<RestrictedAccessStateProps> = ({
    className,
    description,
    iconSize = Size.xxl,
    isVerticallyCentered = false,
    shouldShowDescription = true,
    shouldShowIcon = false,
    theme,
    title,
    titleAs = 'h1',
    wrapperProps,
}) => {
    const { translateKey } = useTranslate();
    const { block } = useClassnames(CLASSNAME);

    return (
        <BaseState
            className={block([className])}
            iconProps={shouldShowIcon ? { icon: mdiAccountCancel, size: iconSize } : undefined}
            title={title || translateKey(GLOBAL.RESTRICTED_ACCESS)}
            titleAs={titleAs}
            theme={theme}
            message={shouldShowDescription ? description : undefined}
            wrapperProps={wrapperProps}
            fillSpace={isVerticallyCentered}
        />
    );
};
